import React, { ReactNode, useContext, useRef } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { CircularProgress, LinearProgress, Box } from "@mui/material";
import { VideoRoom } from "protogen/video_pb";
import { TranscriptionSegment } from "protogen/common_pb";
import { AccountStub } from "protogen/common_pb";
import ScrollableActivityPanel from "./ScrollableActivityPanel";
import ActivityFeedEntry from "components/activity/ActivityFeedEntry";
import MessageBubble from "components/activity/MessageBubble";
import { ScrollContainerHandle } from "components/common/ScrollContainer";
import { getFormattedDuration } from "common/utils";
import { CurrentUserContext } from "components/context/RequireAuth";
import useVideoWebSocketTranscriptions from "components/activity/useVideoWebSocketTranscriptions";

export type ActivityEntry = {
  track: string;
  startSec: number;
  text: string;
  maxTemperature: number;
  segments: TranscriptionSegment[];
};

export const aggregateEntries = (
  segments: TranscriptionSegment[],
): ActivityEntry[] => {
  const entries: ActivityEntry[] = [];
  const _toEntry = (segment: TranscriptionSegment): ActivityEntry => ({
    track: segment.track,
    startSec: segment.startSec,
    text: segment.text,
    maxTemperature: segment.temperature,
    segments: [segment],
  });
  const _mergeEntry = (
    entry: ActivityEntry,
    segment: TranscriptionSegment,
  ): ActivityEntry => ({
    ...entry,
    text: `${entry.text} ${segment.text}`,
    maxTemperature: Math.max(entry.maxTemperature, segment.temperature),
    segments: [...entry.segments, segment],
  });
  let activeEntry: ActivityEntry | null = null;
  for (const segment of segments) {
    if (activeEntry === null) {
      // Start an entry
      activeEntry = _toEntry(segment);
    } else if (activeEntry.track === segment.track && !segment.speechFinal) {
      // Add to entry
      activeEntry = _mergeEntry(activeEntry, segment);
    } else {
      // Close the entry
      entries.push(activeEntry);
      activeEntry = _toEntry(segment);
    }
  }
  // Close the entry
  if (activeEntry !== null) {
    entries.push(activeEntry);
  }
  // Close the entry
  return entries;
};

type Props = {
  videoRoom: VideoRoom | null;
  participants: AccountStub[];
};

const TranscriptionStatus = ({
  videoRoom,
  transcriptionFailed,
}: {
  videoRoom: VideoRoom | null;
  transcriptionFailed: boolean;
  streamStarted: boolean;
  refresh?: () => void;
}) => {
  let message = "";
  let icon: ReactNode = null;
  if (!videoRoom?.transcriptionEnabled || transcriptionFailed) {
    icon = <WarningAmberIcon sx={{ height: 48, width: 48 }} />;
    message = "No Transcript Available";
  } else {
    icon = <CircularProgress sx={{ height: 48, width: 48 }} />;
    message = "Processing transcription";
  }
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "250px",
      }}
    >
      {icon}
      <span style={{ marginLeft: "10px" }}>{message}</span>
    </div>
  );
};

const FeedEntry = ({
  entry,
  sender,
  isCurrentUser,
}: {
  entry: ActivityEntry;
  sender: AccountStub;
  isCurrentUser: boolean;
}) => {
  const [headerOpen, setHeaderOpen] = React.useState(false);
  const alignLeft = !sender?.isAdvisor;
  const loading = entry.segments.some((s) => !s.stable);
  return (
    <ActivityFeedEntry
      alignLeft={alignLeft}
      avatarGutter={false}
      headerLeftText={isCurrentUser ? "You" : `${sender?.firstName || "Guest"}`}
      hideHeader={!headerOpen}
      headerRightText={getFormattedDuration(Number(entry.startSec))}
      sxOverride={{
        padding: "2px 0",
      }}
    >
      <div
        onClick={() => {
          setHeaderOpen((o) => !o);
        }}
      >
        <MessageBubble
          alignLeft={alignLeft}
          sx={loading ? { opacity: 0.5 } : {}}
        >
          {entry.text}
        </MessageBubble>
      </div>
    </ActivityFeedEntry>
  );
};

export default ({ videoRoom, participants }: Props) => {
  const [scrollPercent, setScrollPercent] = React.useState(100);
  const currentUser = useContext(CurrentUserContext);
  const scrollRef = useRef<ScrollContainerHandle | null>(null);
  const { segments, streamStarted, transcriptionFailed } =
    useVideoWebSocketTranscriptions(videoRoom || new VideoRoom(), []);

  const participantsMap = participants.reduce(
    (acc, participant) => {
      acc[participant.ref] = participant;
      return acc;
    },
    {} as Record<string, AccountStub>,
  );

  const entries = aggregateEntries(segments);

  const reversedEntries = [...entries].reverse();
  const scrollEntries = reversedEntries.map((entry, i) => {
    const senderRef = entry.track.split("-")[1];
    const sender = participantsMap[senderRef];
    return (
      <FeedEntry
        key={i}
        entry={entry}
        sender={sender}
        isCurrentUser={currentUser.ref === senderRef}
      />
    );
  });
  return (
    <ScrollableActivityPanel
      sx={{ borderRadius: "0px" }}
      setScrollPercentage={setScrollPercent}
      scrollEntries={[
        // Counterintuitively, this is scroll reverse so pre-pend this.
        ...(scrollEntries.length
          ? scrollEntries
          : [
              <TranscriptionStatus
                key={1}
                videoRoom={videoRoom}
                transcriptionFailed={transcriptionFailed}
                streamStarted={streamStarted}
              />,
            ]),
      ]}
      scrollRef={scrollRef}
      aboveFooterPanel={
        <Box display="flex" flexDirection="column" gap="16px">
          <LinearProgress
            variant="determinate"
            value={scrollPercent}
            sx={{
              height: "2px",
            }}
          />
        </Box>
      }
    />
  );
};
